import { InputAdornment } from '@material-ui/core';
import DataType from '@opsbase/lib-datatype/DataType/DataType';
import i18n from '@opsbase/lib-i18n';
import Flag from '@opsbase/lib-issue/Flag';
import Palette from '@opsbase/lib-palette';
import Validator from '@opsbase/lib-validator';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useIssueCreate from '@opsbase/react-gql-hooks/issue/useIssueCreate';
import PhotoTextInput from '@opsbase/web-conversation-views/src/PhotoTextInput';
import useNewDataActionsContext from '@opsbase/web-ctrl-newdata/useNewDataActions';
import AssetChooser from '@opsbase/web-data-choosers/AssetChooser';
import PlaceChooser from '@opsbase/web-data-choosers/PlaceChooser';
import StaffChooser from '@opsbase/web-data-choosers/StaffChooser';
import CreateDialog from '@opsbase/web-dialogs/CreateDialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import PencilIcon from '@opsbase/web-icons/icons/PencilIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNav from '@opsbase/web-nav/useNav/useNav';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import FlagPicker from '@opsbase/web-pickers/FlagPicker';
import IssueTypePicker from '@opsbase/web-pickers/IssueTypePicker';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Text from '@opsbase/web-ui/Text';
import omit from 'lodash/omit';
import React, { useCallback, useMemo, useState } from 'react';
export function IssueCreateDialog() {
    const self = useSelf();
    const { raise } = useNotificationActions();
    const createIssue = useIssueCreate();
    const [title, setTitle] = useState('');
    const [flag, setFlag] = useState(Flag.BLACK);
    const [type, setType] = useState(undefined);
    const [place, setPlace] = useState(undefined);
    const [asset, setAsset] = useState(undefined);
    const [createdBy, setCreatedBy] = useState(undefined);
    const [commentTitle, setCommentTitle] = useState('');
    const [commentImages, setCommentImages] = useState([]);
    const { addItemsToType } = useNewDataActionsContext();
    const { goBack } = useNav();
    const places = useMemo(() => (place ? [place] : []), [place]);
    const handleChangeTitle = useCallback(newTitle => setTitle(newTitle), []);
    const handleChangePlace = useCallback(newPlaces => {
        setPlace(newPlaces[0]);
        setCreatedBy(undefined);
    }, []);
    const handleChangeCreatedBy = useCallback(newCreatedBy => setCreatedBy(newCreatedBy[0]), []);
    function isValidIssueCreation() {
        return (title.length &&
            place !== undefined &&
            type !== undefined &&
            flag !== undefined &&
            ((self.isSuperUser || !self.isStaff) ? createdBy !== undefined : true));
    }
    const handleCreate = useCallback(async () => {
        var _a;
        const omitValues = [];
        if (!self.isSuperUser) {
            omitValues.push('createdBy');
        }
        Validator.validate(omit({ title, type, place, flag, createdBy }, omitValues));
        return raise(createIssue({
            patch: {
                title,
                flag,
                type,
                place: {
                    id: place.id,
                },
                createdBy: createdBy
                    ? {
                        id: createdBy.id,
                    }
                    : undefined,
                asset: !!(asset === null || asset === void 0 ? void 0 : asset.length) ? { id: (_a = asset[0]) === null || _a === void 0 ? void 0 : _a.id } : undefined,
                comments: commentTitle.length || commentImages.length
                    ? [
                        {
                            title: commentTitle,
                            photos: commentImages === null || commentImages === void 0 ? void 0 : commentImages.map(z => z.fileName).join(','),
                        },
                    ]
                    : [],
            },
        }), i18n.t('issue-created-successful'));
    }, [
        self.isSuperUser,
        asset,
        title,
        type,
        place,
        flag,
        createdBy,
        raise,
        createIssue,
        commentTitle,
        commentImages,
    ]);
    const handleAfterCreate = useCallback(async (issue) => {
        addItemsToType(DataType.ISSUE, [issue]);
        goBack();
    }, [goBack, addItemsToType]);
    const handleChangeAsset = useCallback(newAsset => setAsset(newAsset), []);
    return (React.createElement(CreateDialog, { padded: true, valid: isValidIssueCreation(), size: DialogSize.X550_Y350, title: i18n.t('log-issue'), onCreate: handleCreate, afterCreate: handleAfterCreate },
        React.createElement(Vertical, { gutter: true, shrink: true },
            React.createElement(TextFieldWithStateAndBlur, { white: true, autoFocus: true, fullWidth: true, placeholder: i18n.t('add-issue-title'), value: title, onBlur: handleChangeTitle }),
            React.createElement(Horizontal, { gutter: true },
                React.createElement(Pane, { grow: true },
                    React.createElement(Text, { variant: "h3" }, i18n.t('issue-type')),
                    React.createElement(IssueTypePicker, { noMargin: true, white: true, value: type, onChange: setType, fullWidth: true })),
                React.createElement(Pane, { grow: true },
                    React.createElement(Text, { variant: "h3" }, i18n.t('flag')),
                    React.createElement(FlagPicker, { noMargin: true, fullWidth: true, white: true, value: flag, onChange: setFlag }))),
            React.createElement(Horizontal, { gutter: true },
                React.createElement(Pane, { grow: true },
                    React.createElement(Text, { variant: "h3" }, i18n.t('select-place')),
                    React.createElement(PlaceChooser, { value: places, onChange: handleChangePlace })),
                React.createElement(Pane, { grow: true }, !!place && (self.isSuperUser || !self.isStaff) && (React.createElement(React.Fragment, null,
                    React.createElement(Text, { variant: "h3" }, i18n.t('select-reporter')),
                    React.createElement(StaffChooser, { value: createdBy ? [createdBy] : undefined, onChange: handleChangeCreatedBy, filters: place ? { place: [place.id], team: self.accessibleTeams.ids } : undefined }))))),
            React.createElement(Pane, null,
                React.createElement(AssetChooser, { selectLabel: i18n.t('issue-associate-asset'), multi: false, value: asset, filters: { place: [place === null || place === void 0 ? void 0 : place.id] }, onChange: handleChangeAsset })),
            React.createElement(Horizontal, { gutter: true, style: { backgroundColor: Palette.White.main } },
                React.createElement(Pane, { grow: true },
                    React.createElement(PhotoTextInput, { placeholderText: i18n.t('create-issue-comment-placeholder'), text: commentTitle, onTextChange: setCommentTitle, multiline: true, images: commentImages, onImagesChange: setCommentImages, InputProps: {
                            startAdornment: (React.createElement(InputAdornment, { position: "start" },
                                React.createElement(PencilIcon, null))),
                            style: {
                                borderRadius: 4,
                            },
                        }, editable: true, hideSubmitButton: true }))))));
}
export default IssueCreateDialog;
